// Story 1.30 — TanStack Query mutation: POST /api/v1/admin/bulletin/posts.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { BulletinPostListItem } from "./useAdminBulletinList";

export type CreateBulletinDraftInput = {
  title: string;
  slug: string;
  body_md: string;
};

export type CreateBulletinDraftResponse = {
  data: BulletinPostListItem;
};

async function createDraft(
  input: CreateBulletinDraftInput
): Promise<CreateBulletinDraftResponse> {
  const idempotencyKey = crypto.randomUUID();
  const response = await fetch("/api/v1/admin/bulletin/posts", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(input),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Create bulletin draft failed (HTTP ${response.status})`;
    let code: string | undefined;
    let slug: string | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      code = raw?.error?.code;
      slug = raw?.error?.slug;
    } catch {
      // body not JSON
    }
    const err = new Error(message) as Error & { code?: string; slug?: string };
    err.code = code;
    err.slug = slug;
    throw err;
  }
  return (await response.json()) as CreateBulletinDraftResponse;
}

export function useCreateBulletinDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDraft,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-bulletin-list"] });
    },
  });
}
